import { QueryClient, QueryFunction } from "@tanstack/react-query";

// Globálna premenná pre ukladanie CSRF tokenu
let csrfToken: string | null = null;

async function throwIfResNotOk(res: Response) {
  if (!res.ok) {
    const text = (await res.text()) || res.statusText;
    throw new Error(`${res.status}: ${text}`);
  }
}

// Funkcia na získanie CSRF tokenu zo servera
async function fetchCsrfToken(): Promise<string> {
  // Ak už máme token, použijeme ho
  if (csrfToken) {
    return csrfToken;
  }
  
  try {
    const response = await fetch('/api/csrf-token', {
      credentials: 'include',
    });
    
    if (!response.ok) {
      console.error('Failed to fetch CSRF token');
      return '';
    }
    
    const data = await response.json();
    csrfToken = data.csrfToken;
    return csrfToken || '';
  } catch (error) {
    console.error('Error fetching CSRF token:', error);
    return '';
  }
}

// Funkcia pre resetovanie CSRF tokenu - napr. po vypršaní
export function resetCsrfToken() {
  csrfToken = null;
}

export async function apiRequest(
  method: string,
  url: string,
  data?: unknown | undefined,
): Promise<Response> {
  console.log('🔧 [apiRequest] method:', method, 'url:', url, 'data:', data);
  
  const options: RequestInit = {
    method,
    credentials: "include",
    cache: "no-cache", // Vypnúť cache pre všetky requesty
  };

  // Pridáme CSRF token pre mutačné requesty
  if (method !== 'GET') {
    const token = await fetchCsrfToken();
    options.headers = {
      ...(options.headers || {}),
      'X-CSRF-Token': token,
    };
  }

  if (data) {
    if (data instanceof FormData) {
      options.body = data;
    } else {
      options.headers = { 
        ...(options.headers || {}),
        "Content-Type": "application/json" 
      };
      options.body = JSON.stringify(data);
    }
  }

  const res = await fetch(url, options);
  console.log('🔧 [apiRequest] response status:', res.status, 'statusText:', res.statusText);
  
  // Jednotný 401 handler - redirect na auth ak nie sme už tam
  if (res.status === 401) {
    console.log('🔧 [apiRequest] 401 unauthorized - checking if redirect needed');
    const currentPath = window.location.pathname;
    if (!currentPath.startsWith('/auth') && !currentPath.startsWith('/reset-password')) {
      console.log('🔧 [apiRequest] Redirecting to /auth');
      window.location.href = '/auth';
      throw new Error('401: Unauthorized - redirected to login');
    }
  }
  
  // Ak dostaneme 403 s CSRF chybou, skúsime získať nový token a zopakujeme request
  if (res.status === 403 && res.headers.get('X-CSRF-Error') === 'true') {
    resetCsrfToken();
    return apiRequest(method, url, data);
  }
  
  await throwIfResNotOk(res);
  return res;
}

type UnauthorizedBehavior = "returnNull" | "throw";
export const getQueryFn: <T>(options: {
  on401: UnauthorizedBehavior;
}) => QueryFunction<T> =
  ({ on401: unauthorizedBehavior }) =>
  async ({ queryKey }) => {
    try {
      const res = await fetch(queryKey[0] as string, {
        credentials: "include",
        headers: { 'Accept': 'application/json' },
        cache: 'no-cache'
      });

      if (unauthorizedBehavior === "returnNull" && res.status === 401) {
        // Na /auth je 401 normálne – nevyhadzuj konzolu
        if (window.location.pathname.startsWith('/auth')) {
          return null;
        }
        // Pre ostatné stránky vráť null, ale nevyhadzuj error
        return null;
      }

      await throwIfResNotOk(res);
      return await res.json();
    } catch (error: any) {
      if (unauthorizedBehavior === "returnNull" && error?.message?.includes('401')) {
        // Na /auth je 401 normálne – nevyhadzuj konzolu
        if (window.location.pathname.startsWith('/auth')) {
          return null;
        }
        // Pre ostatné stránky vráť null, ale nevyhadzuj error
        return null;
      }
      throw error;
    }
  };

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      queryFn: getQueryFn({ on401: "throw" }),
      refetchInterval: false,
      refetchOnWindowFocus: false,
      staleTime: 0, // Vypnutý cache pre vývoj
      gcTime: 0, // Vypnutý cache pre vývoj
      retry: (failureCount, error: any) => {
        // Neretry pre 4xx chyby (autentifikácia, oprávnenia) bez console erroru
        if (error?.message?.includes('401') || error?.message?.includes('403')) return false;
        // Retry max 2x pre ostatné chyby
        return failureCount < 2;
      },
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 5000),
    },
    mutations: {
      retry: 1, // Mutations retry iba raz
      retryDelay: 1000,
    },
  },
});